import { FilterQueryType } from "cloud-core/filters/FilterSet";
import { AnalyticsStateView } from "../../../store/analytics/Analytics";
import OptionFilter from "../../../views/viz/components/Filtering/OptionFilter";
import { Entity } from "../Entity";
import { EntityFilter } from "./EntityFilter";
import { StringPropertyGetter } from "./StringEntityFilter";

export type AvailableOption = {
    value: string;
    label: string;
    isFixed: boolean;
    defaultExcluded: boolean;
    isHidden: boolean;
};
export const DEFAULT_OPTION = {
    value: "all",
    label: "All",
    isFixed: true,
    defaultExcluded: false,
    isHidden: false,
} as const;

export type OptionEntityFilterParameter = { options: string[] };

export class OptionEntityFilter extends EntityFilter {
    type: FilterQueryType = "option";
    availableOptions: AvailableOption[];
    predicate: (entities: Entity[], parameter: OptionEntityFilterParameter) => Entity[];

    constructor(
        displayName: string,
        filterRef: string,
        predicate: (entities: Entity[], parameter: OptionEntityFilterParameter) => Entity[],
        availableOptions: { value: string; label: string; defaultExcluded?: boolean }[],
    ) {
        super(displayName, filterRef);
        this.predicate = predicate;
        this.availableOptions = availableOptions.map((o) => ({
            ...o,
            isFixed: false,
            defaultExcluded: o.defaultExcluded ?? false,
            isHidden: false,
        }));
        this.availableOptions.unshift(DEFAULT_OPTION);

        if (this.availableOptions.filter((o) => o.defaultExcluded).length != 0) {
            this.defaultParams = {
                options: this.availableOptions
                    .filter((o) => !o.defaultExcluded && o.value != DEFAULT_OPTION.value)
                    .map((o) => o.value),
            };
        }
    }

    createComponent(filterId: string, view: AnalyticsStateView, options?: unknown): JSX.Element {
        return <OptionFilter filterId={filterId} viewId={view.viewId} availableOptions={this.availableOptions} />;
    }

    createParameterObject(parameter: OptionEntityFilterParameter): unknown {
        return { values: parameter.options };
    }

    parseParameterObject(parameterObject: unknown): OptionEntityFilterParameter {
        return { options: (parameterObject as { values: string[] }).values };
    }
}

export class DataOptionEntityFilter extends OptionEntityFilter {
    availableOptionsGenerator: (entities: Entity[]) => AvailableOption[];

    constructor(
        displayName: string,
        filterRef: string,
        predicate: (entities: Entity[], parameter: OptionEntityFilterParameter) => Entity[],
        availableOptionsGenerator: (entities: Entity[]) => AvailableOption[],
    ) {
        super(displayName, filterRef, predicate, []);
        this.availableOptionsGenerator = availableOptionsGenerator;
    }

    override createComponent(filterId: string, view: AnalyticsStateView, options?: unknown): JSX.Element {
        const availableOptions = this.availableOptionsGenerator(view.entities);
        availableOptions.unshift(DEFAULT_OPTION);

        return <OptionFilter filterId={filterId} viewId={view.viewId} availableOptions={availableOptions} />;
    }

    createParameterObject(parameter: OptionEntityFilterParameter): unknown {
        return { values: parameter.options };
    }

    parseParameterObject(parameterObject: unknown): OptionEntityFilterParameter {
        return { options: (parameterObject as { values: string[] }).values };
    }
}

export function StringPropertyOptionFilterPredicate(
    key: string,
): (entities: Entity[], parameter: OptionEntityFilterParameter) => Entity[] {
    const propertyGetter = StringPropertyGetter(key);

    return (entities: Entity[], parameter: OptionEntityFilterParameter) => {
        return entities.filter((e) => parameter.options.includes(propertyGetter(e)));
    };
}
